import React, { useState } from "react";
import { Gavel, ExternalLink, CheckCircle2, AlertTriangle, X } from "lucide-react";

const NYSCEF_URL = "https://iapps.courts.state.ny.us/nyscef/CaseSearch?TAB=name";

const CourtsCheckButton = ({ lead, api, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [foundCases, setFoundCases] = useState(false);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const existing = lead.courts_check;

  const runCheck = async () => {
    try {
      await navigator.clipboard.writeText(lead.business_name);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      /* clipboard not granted, fine */
    }
    window.open(NYSCEF_URL, "_blank", "noopener,noreferrer");
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/leads/${lead.id}/courts-checked`, {
        found_cases: foundCases,
        notes: notes.trim() || null,
      });
      onSaved && onSaved();
      setOpen(false);
      setFoundCases(false);
      setNotes("");
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="border border-white/5 rounded-lg p-5" style={{ background: "rgba(10,14,23,0.4)" }}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[0.65rem] uppercase tracking-[0.22em] text-cf-gold font-semibold flex items-center gap-1">
              <Gavel size={11} /> NY Courts (NYSCEF) check
            </div>
            {existing ? (
              <div className="text-sm mt-2 flex items-center gap-2">
                {existing.found_cases ? (
                  <span className="text-rose-300 flex items-center gap-1"><AlertTriangle size={12} /> Cases found</span>
                ) : (
                  <span className="text-emerald-300 flex items-center gap-1"><CheckCircle2 size={12} /> Clear</span>
                )}
                <span className="text-cf-muted text-xs">
                  · by {existing.checked_by} on {new Date(existing.checked_at).toLocaleDateString()}
                </span>
              </div>
            ) : (
              <p className="text-cf-muted text-xs mt-1">Not yet checked.</p>
            )}
            {existing?.notes && (
              <p className="text-cf-text text-xs mt-2 italic">&ldquo;{existing.notes}&rdquo;</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={runCheck} className="cf-btn-ghost" style={{ fontSize: "0.7rem" }}>
              <ExternalLink size={12} /> {existing ? "Re-check" : "Run check"}
            </button>
          </div>
        </div>
        {copied && <div className="text-[0.65rem] text-cf-gold-light mt-2">✓ Business name copied — paste into NYSCEF.</div>}
      </div>

      {open && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(5,7,10,0.75)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 9999, padding: 20,
          }}
          onClick={() => setOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#0A0E17", border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 14, width: "100%", maxWidth: 460, color: "#F8F9FA",
              padding: 24, fontFamily: "Manrope, sans-serif",
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="text-[0.65rem] uppercase tracking-[0.22em] text-cf-gold font-semibold flex items-center gap-1">
                <Gavel size={11} /> Log NY Courts result
              </div>
              <button onClick={() => setOpen(false)} className="text-cf-muted hover:text-cf-text">
                <X size={16} />
              </button>
            </div>
            <div className="text-cf-text text-sm mb-1 font-semibold">{lead.business_name}</div>
            <p className="text-cf-muted text-xs mb-5">
              After searching NYSCEF in the new tab, log what you found here.
            </p>

            <div className="space-y-3">
              <label className="flex items-center gap-3 p-3 border border-white/5 rounded-lg cursor-pointer" style={{ background: foundCases ? "rgba(244,63,94,0.08)" : "transparent" }}>
                <input type="radio" name="cc" checked={foundCases} onChange={() => setFoundCases(true)} />
                <div className="flex-1">
                  <div className="text-sm">Cases found</div>
                  <div className="text-xs text-cf-muted">Active or recent litigation / judgments / defaults.</div>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 border border-white/5 rounded-lg cursor-pointer" style={{ background: !foundCases ? "rgba(16,185,129,0.08)" : "transparent" }}>
                <input type="radio" name="cc" checked={!foundCases} onChange={() => setFoundCases(false)} />
                <div className="flex-1">
                  <div className="text-sm">Clear</div>
                  <div className="text-xs text-cf-muted">No relevant cases in NYSCEF.</div>
                </div>
              </label>
            </div>

            <textarea
              value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Notes (case # / amount / status / any concerns)…" rows={3}
              className="cf-input mt-4 resize-none w-full"
            />

            <div className="flex gap-2 mt-5 justify-end">
              <button onClick={() => setOpen(false)} className="cf-btn-ghost" style={{ fontSize: "0.7rem" }}>Cancel</button>
              <button onClick={save} disabled={saving} className="cf-btn-ghost" style={{ fontSize: "0.7rem", background: "#B68A35", color: "#fff", borderColor: "#B68A35" }}>
                {saving ? "Saving…" : "Save result"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CourtsCheckButton;
